import React, { useEffect, useState } from 'react';
import { hrmsGetHandbook, hrmsSaveHandbook, hrmsResetHandbook, hrmsAcknowledgeHandbook, hrmsMyOnboarding } from '../../lib/hrmsApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { CheckCircle2, BookOpen, Sparkles, Pencil, X, Plus, Trash2, ChevronUp, ChevronDown, RotateCcw, Save } from 'lucide-react';

// Very small markdown renderer for **bold**, - lists and newlines (safe enough for internal handbook content)
const renderMd = (md) => {
  const lines = (md || '').split('\n');
  const out = [];
  let list = [];
  const flushList = () => { if (list.length) { out.push(<ul key={`ul-${out.length}`} className="list-disc list-inside space-y-1 text-slate-700 mt-2 ml-1">{list.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: li }} />)}</ul>); list = []; } };
  const fmt = (s) => s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-orange-600 underline">$1</a>');
  lines.forEach((line, idx) => {
    if (line.startsWith('- ')) { list.push(fmt(line.slice(2))); return; }
    flushList();
    if (line.trim() === '') { out.push(<div key={`br-${idx}`} className="h-2" />); return; }
    out.push(<p key={idx} className="text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: fmt(line) }} />);
  });
  flushList();
  return out;
};

const HandbookPage = () => {
  const { toast } = useToast();
  const [hb, setHb] = useState(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const user = (() => { try { return JSON.parse(localStorage.getItem('hrms_user') || 'null'); } catch { return null; } })();
  const canEdit = user && ['super_admin', 'hr_admin'].includes(user.role);

  useEffect(() => {
    hrmsGetHandbook().then(setHb).catch(() => {});
    hrmsMyOnboarding().then((o) => setAck(!!o.handbook_acknowledged_at)).catch(() => {});
  }, []);

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(hb)));
    setEditing(true);
  };
  const cancelEdit = () => { setDraft(null); setEditing(false); };
  const saveEdit = async () => {
    if (!(draft.sections || []).length) { toast({ title: 'Add at least one section' }); return; }
    setSaving(true);
    try {
      const saved = await hrmsSaveHandbook({
        title: draft.title, subtitle: draft.subtitle || '', version: draft.version || 'v1',
        sections: draft.sections,
      });
      setHb(saved);
      setEditing(false);
      toast({ title: 'Handbook saved', description: `${saved.sections.length} sections live` });
    } catch (err) { toast({ title: 'Save failed', description: err?.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };
  const resetToDefault = async () => {
    if (!window.confirm('Reset to the built-in default handbook? Your custom edits will be lost.')) return;
    try { await hrmsResetHandbook(); const fresh = await hrmsGetHandbook(); setHb(fresh); setEditing(false); toast({ title: 'Handbook reset' }); }
    catch (err) { toast({ title: 'Reset failed', description: err?.response?.data?.detail || '' }); }
  };
  const updateSection = (i, patch) => {
    const next = { ...draft };
    next.sections = draft.sections.map((s, idx) => idx === i ? { ...s, ...patch } : s);
    setDraft(next);
  };
  const removeSection = (i) => setDraft({ ...draft, sections: draft.sections.filter((_, idx) => idx !== i) });
  const moveSection = (i, dir) => {
    const next = [...draft.sections];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setDraft({ ...draft, sections: next });
  };
  const addSection = () => setDraft({ ...draft, sections: [...(draft.sections || []), { id: `s-${Date.now()}`, title: 'New section', body_md: '' }] });

  const acknowledge = async () => {
    setBusy(true);
    try {
      await hrmsAcknowledgeHandbook();
      setAck(true);
      toast({ title: 'Handbook acknowledged', description: 'Onboarding task auto-marked complete.' });
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || '' });
    } finally { setBusy(false); }
  };

  if (!hb) return <div className="text-slate-500 text-sm">Loading handbook…</div>;

  // Edit mode
  if (editing && draft) {
    return (
      <div data-testid="handbook-page" className="max-w-3xl mx-auto">
        <div className="rounded-2xl bg-slate-900 text-white p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-300 mb-1">Editing handbook</div>
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="bg-slate-800 border-slate-700 text-white text-lg font-bold h-11" placeholder="Title" data-testid="hb-edit-title" />
              <Input value={draft.subtitle || ''} onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })} className="mt-2 bg-slate-800 border-slate-700 text-white text-sm" placeholder="Subtitle" data-testid="hb-edit-subtitle" />
              <Input value={draft.version || ''} onChange={(e) => setDraft({ ...draft, version: e.target.value })} className="mt-2 bg-slate-800 border-slate-700 text-white text-xs h-9 w-32" placeholder="v1.0" data-testid="hb-edit-version" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={cancelEdit} className="bg-transparent border-white/30 text-white hover:bg-white/10"><X className="h-4 w-4 mr-1" /> Cancel</Button>
              <Button onClick={saveEdit} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600" data-testid="hb-save"><Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {(draft.sections || []).map((s, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`hb-edit-section-${i}`}>
              <div className="flex items-center gap-2">
                <div className="font-mono text-xs text-slate-400 shrink-0">{String(i + 1).padStart(2, '0')}</div>
                <Input value={s.title} onChange={(e) => updateSection(i, { title: e.target.value })} className="font-bold" placeholder="Section title" />
                <Button variant="outline" onClick={() => moveSection(i, -1)} disabled={i === 0} className="h-8 w-8 p-0"><ChevronUp className="h-4 w-4" /></Button>
                <Button variant="outline" onClick={() => moveSection(i, 1)} disabled={i === draft.sections.length - 1} className="h-8 w-8 p-0"><ChevronDown className="h-4 w-4" /></Button>
                <Button variant="outline" onClick={() => removeSection(i)} className="h-8 w-8 p-0 border-red-200 text-red-600"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <Textarea value={s.body_md} onChange={(e) => updateSection(i, { body_md: e.target.value })} rows={5} className="mt-3 font-mono text-sm" placeholder="Body (Markdown: **bold**, - bullets, [link](url))" data-testid={`hb-edit-body-${i}`} />
              {s.body_md && (
                <details className="mt-2">
                  <summary className="text-xs text-slate-500 cursor-pointer">Preview</summary>
                  <div className="mt-2 p-3 bg-slate-50 rounded-lg text-sm">{renderMd(s.body_md)}</div>
                </details>
              )}
            </div>
          ))}
          <Button onClick={addSection} variant="outline" className="w-full border-dashed" data-testid="hb-add-section"><Plus className="h-4 w-4 mr-1" /> Add section</Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="handbook-page" className="max-w-3xl mx-auto">
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-300"><BookOpen className="h-4 w-4" /> {hb.version}</div>
            <h1 className="text-3xl md:text-4xl font-extrabold mt-1">{hb.title}</h1>
            {hb.subtitle && <p className="mt-2 text-slate-300 max-w-2xl">{hb.subtitle}</p>}
          </div>
          {canEdit && (
            <div className="flex gap-2 shrink-0">
              <Button onClick={startEdit} variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10" data-testid="hb-edit-button"><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
              {user?.role === 'super_admin' && <Button onClick={resetToDefault} variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10" data-testid="hb-reset-button"><RotateCcw className="h-4 w-4 mr-1" /> Reset</Button>}
            </div>
          )}
        </div>
      </div>

      {/* TOC */}
      <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Table of contents</div>
        <ol className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(hb.sections || []).map((s, i) => (
            <li key={s.id}><a href={`#hb-${s.id}`} className="text-sm text-slate-700 hover:text-orange-600 flex items-start gap-2"><span className="font-mono text-slate-400 shrink-0">{String(i + 1).padStart(2, '0')}</span> {s.title}</a></li>
          ))}
        </ol>
      </div>

      {/* Sections */}
      <div className="mt-6 space-y-6">
        {(hb.sections || []).map((s, i) => (
          <section key={s.id} id={`hb-${s.id}`} className="rounded-2xl bg-white border border-slate-200 p-6 scroll-mt-6" data-testid={`hb-section-${s.id}`}>
            <div className="flex items-baseline gap-3">
              <div className="font-mono text-xs text-slate-400">{String(i + 1).padStart(2, '0')}</div>
              <h2 className="text-xl font-extrabold text-slate-900">{s.title}</h2>
            </div>
            <div className="mt-3 space-y-2 text-sm">{renderMd(s.body_md)}</div>
          </section>
        ))}
      </div>

      {/* Acknowledgment */}
      <div className={`mt-8 rounded-2xl border p-6 ${ack ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}`} data-testid="hb-ack-card">
        {ack ? (
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            <div>
              <div className="font-bold text-emerald-800">You've acknowledged the handbook — thank you!</div>
              <div className="text-xs text-emerald-700">Your onboarding checklist has been updated automatically.</div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Sparkles className="h-8 w-8 text-orange-500 shrink-0" />
            <div className="flex-1">
              <div className="font-bold text-slate-900">Have you read through everything above?</div>
              <div className="text-sm text-slate-600 mt-0.5">Acknowledging the handbook completes an onboarding task.</div>
            </div>
            <Button onClick={acknowledge} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="hb-ack-button">
              {busy ? 'Saving…' : 'I acknowledge'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HandbookPage;
